import React, {ReactElement} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {Dropdown, HarvestIcon, IconText, IconTextIcon} from '@/web-components';
import _ from 'lodash';
import {useIntl} from 'react-intl';

import DeprecatedProjectIndicatorJS from '../../project-indicator/js/DeprecatedProjectIndicatorJS';
import {customStageGroupsOf, showsOpportunityGroup, stageGroupKeyOf, stageLabel} from '../../../util/projectStageUtil';
import {getProjectStages} from '../../../util/ProjectStageCache';
import {ProjectDropdown_projects$data} from './__generated__/ProjectDropdown_projects.graphql';

// harvestProject is not in the fragment, so under Relay's data masking it is never set.
type ProjectNode = NonNullable<ProjectDropdown_projects$data[number]['node']> & {readonly harvestProject?: unknown};

/** The projects as this dropdown reads them: every edge has its node. */
type ProjectEdge = {readonly node: ProjectNode};

type Props = {
	projects: ReadonlyArray<ProjectEdge>;
	emphasizeEmptyState?: boolean;
	maxHeight?: number;
	dropdownAlignment?: 'left' | 'center' | 'right';
	width?: number;
	name?: string;
	selectedItems?: ReadonlyArray<unknown>;
	onRemove?: (...args: unknown[]) => unknown;
	onSelect?: (...args: unknown[]) => unknown;
	optionsName?: string;
	selectedGroupName?: string;
	userpilot?: boolean;
	isMultiSelect?: boolean;
	isClearable?: boolean;
	groupByStatus?: boolean;
	showRunning?: boolean;
	showPlanning?: boolean;
	showOpportunity?: boolean;
	showHalted?: boolean;
	showDone?: boolean;
	showSecondaryIcon?: boolean;
	isHarvestUser?: boolean;
	cy?: string;
	disabled?: boolean;
	label?: string;
	required?: boolean;
	size?: string;
	usePortal?: boolean;
	headerLines?: ReactElement;
};

export const ProjectDropdown = ({
	projects,
	emphasizeEmptyState,
	maxHeight,
	dropdownAlignment,
	width,
	name,
	selectedItems,
	onRemove = () => false,
	onSelect = () => false,
	optionsName,
	selectedGroupName,
	userpilot,
	isMultiSelect,
	isClearable,
	groupByStatus,
	showRunning = true,
	showPlanning = true,
	showOpportunity = true,
	showHalted = true,
	showDone = true,
	showSecondaryIcon,
	isHarvestUser,
	cy,
	disabled,
	label,
	required,
	size,
	usePortal,
	headerLines,
}: Props): ReactElement => {
	const intl = useIntl();
	const projectLabel = intl.formatMessage({id: 'common.project'});
	const dropdownName = name === undefined ? projectLabel : name;
	const optionsGroupName = optionsName === undefined ? projectLabel : optionsName;
	const selectedName = selectedGroupName === undefined ? intl.formatMessage({id: 'common.selected'}) : selectedGroupName;

	const sortedProjects = [...projects].sort((a, b) => {
		const aName = a.node.name || 'Untitled';
		const bName = b.node.name || 'Untitled';
		if (aName.toLowerCase() < bName.toLowerCase()) return -1;
		if (aName.toLowerCase() > bName.toLowerCase()) return 1;
		return 0;
	});

	const groupProjectsByStatus = () => {
		const groupedProjects = _.groupBy(sortedProjects, project => stageGroupKeyOf(project.node));
		return groupedProjects;
	};

	const getLine = (project: ProjectNode): ReactElement => {
		const {id, name, customProjectId, companyProjectId, harvestProject} = project;
		return showSecondaryIcon ? (
			<IconTextIcon
				cy={`option-${name}`}
				key={id}
				value={id}
				text={name}
				searchString={`P${companyProjectId} ${customProjectId} ${name}`}
			>
				<DeprecatedProjectIndicatorJS project={project} disableLink noWidthLimit={false} />
				{harvestProject && isHarvestUser ? <HarvestIcon /> : <div />}
			</IconTextIcon>
		) : (
			<IconText
				cy={`option-${name}`}
				key={id}
				value={id}
				text={name}
				searchString={`P${companyProjectId} ${customProjectId} ${name}`}
			>
				<DeprecatedProjectIndicatorJS project={project} disableLink noWidthLimit={false} />
			</IconText>
		);
	};

	const getGroups = (): ReactElement[] => {
		const groups: ReactElement[] = [];
		if (groupByStatus) {
			const groupedByStatus = groupProjectsByStatus();
			if (!groupedByStatus.PLANNING) groupedByStatus.PLANNING = [];
			if (!groupedByStatus.RUNNING) groupedByStatus.RUNNING = [];
			if (!groupedByStatus.OPPORTUNITY) groupedByStatus.OPPORTUNITY = [];
			if (!groupedByStatus.HALTED) groupedByStatus.HALTED = [];
			if (!groupedByStatus.DONE) groupedByStatus.DONE = [];

			const planningGroup = (
				<Dropdown.Group key={'PLANNING'} name={intl.formatMessage({id: 'project_status.planning'})}>
					{groupedByStatus.PLANNING
						? groupedByStatus.PLANNING.map(edge => {
								return getLine(edge.node);
						  })
						: []}
				</Dropdown.Group>
			);
			const runningGroup = (
				<Dropdown.Group key={'RUNNING'} name={intl.formatMessage({id: 'project_status.running'})}>
					{groupedByStatus.RUNNING
						? groupedByStatus.RUNNING.map(edge => {
								return getLine(edge.node);
						  })
						: []}
				</Dropdown.Group>
			);
			const opportunityGroup = (
				<Dropdown.Group key={'OPPORTUNITY'} name={intl.formatMessage({id: 'project_status.opportunity'})}>
					{groupedByStatus.OPPORTUNITY
						? groupedByStatus.OPPORTUNITY.map(edge => {
								return getLine(edge.node);
						  })
						: []}
				</Dropdown.Group>
			);
			const haltedGroup = (
				<Dropdown.Group key={'HALTED'} name={intl.formatMessage({id: 'project_status.halted'})}>
					{groupedByStatus.HALTED
						? groupedByStatus.HALTED.map(edge => {
								return getLine(edge.node);
						  })
						: []}
				</Dropdown.Group>
			);
			const doneGroup = (
				<Dropdown.Group key={'DONE'} name={intl.formatMessage({id: 'project_status.done'})}>
					{groupedByStatus.DONE
						? groupedByStatus.DONE.map(edge => {
								return getLine(edge.node);
						  })
						: []}
				</Dropdown.Group>
			);

			if (showPlanning) groups.push(planningGroup);
			if (showRunning) groups.push(runningGroup);
			if (
				showsOpportunityGroup(
					showOpportunity,
					groupedByStatus.OPPORTUNITY.map(edge => edge.node)
				)
			)
				groups.push(opportunityGroup);
			if (showHalted) groups.push(haltedGroup);
			if (showDone) groups.push(doneGroup);
			customStageGroupsOf(
				sortedProjects.map(edge => edge.node),
				getProjectStages()
			).forEach(({key, stage}) => {
				groups.push(
					<Dropdown.Group key={key} name={stageLabel(stage, intl)}>
						{(groupedByStatus[key] ?? []).map(edge => getLine(edge.node))}
					</Dropdown.Group>
				);
			});
		} else {
			groups.push(
				<Dropdown.Group name={optionsGroupName} key={'dropdown-options'}>
					{sortedProjects.map(edge => {
						return getLine(edge.node);
					})}
				</Dropdown.Group>
			);
		}

		return groups;
	};

	return (
		<Dropdown
			cy={cy}
			emphasizeEmptyState={emphasizeEmptyState}
			headerLines={headerLines}
			isNested
			isMultiSelect={isMultiSelect}
			isClearable={isClearable}
			onRemove={onRemove}
			onSelect={onSelect}
			selectedGroupName={selectedName}
			selectedItems={selectedItems}
			width={width}
			maxHeight={maxHeight}
			dropdownAlignment={dropdownAlignment}
			name={dropdownName}
			userpilot={userpilot}
			disabled={disabled}
			label={label}
			required={required}
			size={size}
			usePortal={usePortal}
		>
			{getGroups()}
		</Dropdown>
	);
};

export default createFragmentContainer(ProjectDropdown, {
	projects: graphql`
		fragment ProjectDropdown_projects on ProjectTypeEdge @relay(plural: true) {
			node {
				id
				name
				companyProjectId
				customProjectId
				stage {
					id
					name
					profile
				}
				...DeprecatedProjectIndicatorJS_project
			}
		}
	`,
});
